/**
 * Brand-colour utilities shared by every game mechanic.
 *
 * Games are themed at runtime by the brand kit's `primary` / `accent` hex —
 * NOT by the static `--color-xk-*` tokens (those are the admin chrome). These
 * helpers consolidate the `shade()` that every game used to re-declare, plus
 * the contrast logic that fixes the "dark brand disappears on a dark stage"
 * bug surfaced in the visual audit.
 *
 * All helpers accept `#RGB`, `#RRGGBB`, or `rgb()/rgba()` and degrade
 * gracefully (return the input unchanged) on anything unparseable.
 */

export interface RGB {
  r: number;
  g: number;
  b: number;
}

const clamp = (n: number, lo = 0, hi = 255) => Math.max(lo, Math.min(hi, n));

/** Parse a colour string into RGB. Returns null when it can't be parsed. */
export function parseColor(input: string): RGB | null {
  if (!input) return null;
  const s = input.trim();
  const hexMatch = s.replace('#', '');
  if (/^[0-9a-fA-F]{3}$/.test(hexMatch)) {
    return {
      r: parseInt(hexMatch[0] + hexMatch[0], 16),
      g: parseInt(hexMatch[1] + hexMatch[1], 16),
      b: parseInt(hexMatch[2] + hexMatch[2], 16),
    };
  }
  if (/^[0-9a-fA-F]{6}$/.test(hexMatch)) {
    return {
      r: parseInt(hexMatch.slice(0, 2), 16),
      g: parseInt(hexMatch.slice(2, 4), 16),
      b: parseInt(hexMatch.slice(4, 6), 16),
    };
  }
  const rgb = s.match(/rgba?\(([^)]+)\)/i);
  if (rgb) {
    const parts = rgb[1].split(',').map((p) => parseFloat(p));
    if (parts.length >= 3) return { r: parts[0], g: parts[1], b: parts[2] };
  }
  return null;
}

const toRgbString = ({ r, g, b }: RGB) =>
  `rgb(${Math.round(clamp(r))},${Math.round(clamp(g))},${Math.round(clamp(b))})`;

/** Darken toward black by `factor` (0..1). 0.7 = 70% of original brightness. */
export function shade(hex: string, factor: number): string {
  const c = parseColor(hex);
  if (!c) return hex;
  return toRgbString({ r: c.r * factor, g: c.g * factor, b: c.b * factor });
}

/** Lighten toward white by `amount` (0..1). 0.2 = 20% toward white. */
export function tint(hex: string, amount: number): string {
  const c = parseColor(hex);
  if (!c) return hex;
  return toRgbString({
    r: c.r + (255 - c.r) * amount,
    g: c.g + (255 - c.g) * amount,
    b: c.b + (255 - c.b) * amount,
  });
}

/** Mix two colours by ratio `t` (0 = a, 1 = b). */
export function mix(a: string, b: string, t: number): string {
  const ca = parseColor(a);
  const cb = parseColor(b);
  if (!ca || !cb) return a;
  return toRgbString({
    r: ca.r + (cb.r - ca.r) * t,
    g: ca.g + (cb.g - ca.g) * t,
    b: ca.b + (cb.b - ca.b) * t,
  });
}

/** Append an alpha channel, returning an `rgba()` string. `a` is 0..1. */
export function withAlpha(hex: string, a: number): string {
  const c = parseColor(hex);
  if (!c) return hex;
  return `rgba(${Math.round(clamp(c.r))},${Math.round(clamp(c.g))},${Math.round(
    clamp(c.b),
  )},${Math.max(0, Math.min(1, a))})`;
}

/** WCAG relative luminance (0 = black, 1 = white). */
export function luminance(hex: string): number {
  const c = parseColor(hex);
  if (!c) return 0;
  const lin = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(c.r) + 0.7152 * lin(c.g) + 0.0722 * lin(c.b);
}

/** True when the colour is visually dark (needs light text/elements on top). */
export function isDark(hex: string): boolean {
  return luminance(hex) < 0.4;
}

/** Pick near-black or white text that reads on `bg`. */
export function readableText(bg: string): string {
  return isDark(bg) ? '#FFFFFF' : '#0B0B12';
}

/** WCAG contrast ratio between two colours (1..21). */
export function contrastRatio(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Guarantee `color` is visible against `against`. If contrast is too low,
 * nudge it lighter (on a dark bg) or darker (on a light bg) until it reads.
 * Fixes the wheel-segment / element vanishing into a same-hue stage.
 */
export function ensureContrast(color: string, against: string, min = 1.6): string {
  let out = color;
  const lighten = isDark(against);
  for (let i = 0; i < 8 && contrastRatio(out, against) < min; i++) {
    out = lighten ? tint(out, 0.16) : shade(out, 0.84);
  }
  return out;
}

/**
 * The unified branded stage background. Derives a deep, slightly-tinted
 * gradient from the brand so every game shares one premium surface instead of
 * the per-game hardcoded hex (Plinko `#160C2E`, Soccer green, …). Works for
 * both dark and light brands.
 */
export function stageGradient(primary: string, accent: string): string {
  const base = isDark(primary) ? shade(primary, 0.42) : shade(primary, 0.3);
  const deep = isDark(primary) ? shade(primary, 0.16) : shade(primary, 0.12);
  const glow = withAlpha(accent, 0.18);
  return `radial-gradient(120% 80% at 50% -10%, ${glow} 0%, transparent 55%), linear-gradient(160deg, ${base} 0%, ${deep} 100%)`;
}

/** Neutral translucent surface for inner panels — reads on any brand stage. */
export const SURFACE_LIGHT = 'rgba(255,255,255,0.10)';
export const SURFACE_LINE = 'rgba(255,255,255,0.14)';
export const SURFACE_LINE_STRONG = 'rgba(255,255,255,0.28)';
